import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import ws from '../WorldsPage.module.scss';

export type ModalSize = 'sm' | 'md' | 'wide' | 'xl';

const SIZE_CLASS: Record<ModalSize, string | undefined> = {
  sm: ws.modalSm,
  md: undefined,
  wide: ws.modalWide,
  xl: ws.modalXl,
};

/** Pile des modales ouvertes : seule celle du dessus réagit à Échap (confirmation sur une modale). */
const stack: number[] = [];
let seq = 0;

/**
 * Modale de la page Mondes (habillage des modales de Configuration). Échap et clic sur le fond
 * ferment, sauf pendant un enregistrement (`busy`). L'attribut data-worlds-modal permet aux
 * raccourcis de la carte de se taire tant qu'une modale est ouverte.
 *
 * `onSubmit` : le corps et le pied sont dans un <form>, Entrée dans un champ enregistre. Le bouton
 * principal du pied passe alors en type="submit" (désactivé : Entrée ne fait rien), les autres
 * restent en type="button".
 */
export function Modal({
  title, onClose, children, footer, wide = false, busy = false, size, icon, iconTone = 'primary', subtitle, onSubmit,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Ancien nom de size="wide", toujours accepté. */
  wide?: boolean;
  busy?: boolean;
  /** sm 460 px, md 640 px (défaut), wide 860 px, xl 1080 px. */
  size?: ModalSize;
  /** Avant le titre : pastille (ColorChip), drapeau (FlagThumb) ou icône lucide (posée sur un carré teinté). */
  icon?: ReactNode;
  /** Teinte du carré d'une icône lucide. */
  iconTone?: 'primary' | 'warn' | 'danger';
  /** Ligne sous le titre (« idx 12 · modifié le … par … »). */
  subtitle?: ReactNode;
  onSubmit?: () => void;
}) {
  const [id] = useState(() => ++seq);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    stack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || stack[stack.length - 1] !== id) return;
      // Recherche non vide (SearchField) : Échap vide d'abord le champ, la modale reste ouverte.
      // Le champ s'en charge lui-même et arrête la propagation.
      const t = e.target;
      if (t instanceof HTMLInputElement && t.dataset.escClears != null && t.value !== '') return;
      // Échap ne remonte pas jusqu'à la carte (plein écran, geste en cours).
      e.stopPropagation();
      if (!busyRef.current) onCloseRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      const i = stack.lastIndexOf(id);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [id]);

  const sz: ModalSize = size ?? (wide ? 'wide' : 'md');
  const content = (
    <>
      <div className={ws.modalBody}>{children}</div>
      {footer && <div className={ws.modalFoot}>{footer}</div>}
    </>
  );

  return (
    <div className={ws.modalBackdrop} data-worlds-modal
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className={[ws.modal, SIZE_CLASS[sz]].filter(Boolean).join(' ')} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className={ws.modalHead}>
          <div className={ws.modalHeadMain}>
            {icon && <span className={`${ws.modalIcon} ${ws[`modalIcon_${iconTone}`] ?? ''}`}>{icon}</span>}
            <div className={ws.modalHeadText}>
              <h2 id={titleId} className={ws.modalTitle}>{title}</h2>
              {subtitle && <div className={ws.modalSub}>{subtitle}</div>}
            </div>
          </div>
          <button type="button" className={ws.modalClose} onClick={onClose} disabled={busy} aria-label="Fermer"><X size={16} /></button>
        </div>
        {onSubmit ? (
          <form className={ws.modalForm} noValidate
            onSubmit={(e) => { e.preventDefault(); if (!busy) onSubmit(); }}>
            {content}
          </form>
        ) : content}
      </div>
    </div>
  );
}

/** Une modale de la page est-elle ouverte ? (les raccourcis globaux s'effacent alors) */
export function worldsModalOpen(): boolean {
  return !!document.querySelector('[data-worlds-modal]');
}
